import API from './apiClient';

interface SubDeviceReading {
  id: number;
  battery_level: number | null;
  status: string | null;
  temperature: number | null;
  sub_device: SubDevice;
  is_disconnected: boolean;
  disconnected_at: string | null;
}

interface SensorReading {
  created_at?: string;
  reading_id: number;
  device_id?: number;
  device_name?: string | null;
  mac_address?: string;
  reading_time: string;          // ISO 8601
  notification_str?: string | null;
  status: string | null;
  warning_type: string | null;
  warnings: string[] | null;
  latitude: string | null;
  longitude: string | null;
  temperature: number | null;
  co: number | null;
  co2: number | null;
  voc: number | null;
  aqi: number | null;
  aqi_accuracy: number | null;
  ahi: number | null;
  pressure: number | null;
  humidity: number | null;
  battery_level: number | null;
  action: string | null;
  escalation_level: EscalationLevel | null;
  aqi_escalation_level: EscalationLevel | null;
  mci_escalation_level: EscalationLevel | null;
  sub_device_readings?: SubDeviceReading[];
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface Device {
  device_id: number;
  mac_address: string;
  device_name: string | null;
  is_active: boolean;
  last_reading: SensorReading | null;
  sub_devices?: SubDevice[];
  vehicles?: Vehicle[];
  device_type: 'PERSONAL' | 'COMMERCIAL';
}

interface DeviceWithReadings extends Device {
  sensor_readings: SensorReading[];
}

interface Vehicle {
  vehicle_id: number;
  registration_no: string;
  brand: string;
  model: string;
  color: string | null;
  manufacture_year: number;
  device: Device;
}

interface VehicleDetail extends Vehicle {
  device: Device;
}

interface SubDevice {
  id: number;
  mac_address: string;
  sub_device_name: string | null;
  sub_device_type: string;
  is_active: boolean;
  device?: Device;
  baby?: Baby;
}

type EscalationLevel = 'clear' | 'alert' | 'warning' | 'danger';

interface Baby {
  id: number;
  name: string;
  date_of_birth: string;
  age: string;
  avatar_url: string | null;
}

interface BabyDetail extends Baby {
  medical_notes: string | null;
  sub_devices: BabyDetailSubDevice[];
  vehicles: Vehicle[];
  monitor: BabyDetailMonitor | null;
}

interface BabyDetailSubDeviceReading {
  id: number;
  battery_level: number | null;
  status: string | null;
  temperature: number | null;
  is_disconnected: boolean;
  disconnected_at: string | null;
}

interface BabyDetailSubDevice extends SubDevice {
  latest_sub_device_reading: BabyDetailSubDeviceReading | null;
  is_connected: boolean;
}

interface BabyDetailMonitor {
  device_id: number;
  mac_address: string;
  device_name: string | null;
  device_type: 'PERSONAL' | 'COMMERCIAL';
  is_active: boolean;
  last_reading: SensorReading | null;
  connected_devices_count: number;
  active_device_ids: number[];
}

// New interface for creating/updating devices
interface DeviceRequest {
  mac_address: string;
  device_name?: string | null;
  is_active?: boolean;
}

interface BabyRequest {
  name: string;
  date_of_birth: string;
  medical_notes?: string | null;
  sub_devices_ids?: number[];
  avatar_url?: File | null;
}

interface EnvironmentMonitorSubDevice {
  id: number;
  sub_device_name: string;
  sub_device_type: string;
  is_active: boolean;
  baby: {
    id: number;
    name: string;
    escalation_level: EscalationLevel;
  } | null;
  display_name: string;
  is_connected: boolean;
}

interface EnvironmentMonitorDevice {
  device_id: number;
  device_name: string;
  mac_address: string;
  is_active: boolean;
  sub_devices_active: boolean;
  active_sub_devices: number[];
  escalation_level: EscalationLevel;
  sub_devices: EnvironmentMonitorSubDevice[];
}

interface EnvironmentMonitorsResponse {
  environment_monitors: EnvironmentMonitorDevice[];
}

// Interfaces for babies latest readings by vehicle endpoint
interface VehicleInfo {
  vehicle_id: number;
  registration_no: string;
  brand: string;
  model: string;
  color: string | null;
  manufacture_year: number;
  owner_name: string;
}

interface LocationInfo {
  latitude: string;
  longitude: string;
  last_updated: string;
}


interface ConnectedDevice {
  sub_device_id: number;
  sub_device_name: string;
  sub_device_type: string;
  mac_address: string;
  is_active: boolean;
}

interface LatestSubDeviceReading {
  reading_id: number;
  sub_device_id: number;
  sub_device_name: string;
  sub_device_type: string;
  mac_address: string;
  reading_time: string;
  temperature: number | null;
  battery_level: number | null;
  status: string | null;
  is_disconnected: boolean;
  disconnected_at: string | null;
  meta: string | null;
}

interface BabyWithDeviceAndReading {
  baby_id: number;
  name: string;
  date_of_birth: string;
  age: string;
  medical_notes: string | null;
  avatar_url: string | null;
  escalation_level: EscalationLevel;
  is_connected: boolean;
  connected_device: ConnectedDevice;
  latest_sub_device_reading: LatestSubDeviceReading | null;
}

interface VehicleWithBabiesAndReadings {
  vehicle_info: VehicleInfo;
  location: LocationInfo;
  main_device_reading: SensorReading | null;
  device_type: 'PERSONAL' | 'COMMERCIAL';
  babies: BabyWithDeviceAndReading[];
  babies_count: number;
  sub_devices_active?: boolean;
  active_sub_devices?: number[];
}

interface BabiesLatestReadingsByVehicleResponse {
  vehicles: VehicleWithBabiesAndReadings[];
  total_vehicles: number;
  total_babies: number;
}

const URLS = {
  VEHICLES: '/monitoring/vehicles',
  VEHICLES_ID: (vehicleId: number) => `/monitoring/vehicles/${vehicleId}`,
  DEVICES: '/monitoring/devices/',
  DEVICE_ID: (deviceId: number) => `/monitoring/devices/${deviceId}/`,
  DEVICE_READINGS: (deviceId: number) => `/monitoring/devices/${deviceId}/readings/`,
  BABIES: '/monitoring/babies/',
  BABIES_ID: (babyId: number) => `/monitoring/babies/${babyId}/`,
  ENVIRONMENT_MONITORS: '/monitoring/environment-monitors',
  BABIES_LATEST_READINGS_BY_VEHICLE: '/monitoring/babies-latest-readings-by-vehicle',
}

const fetchVehicleDetailAPI = (vehicleId: number) =>
  API.get<VehicleDetail>(URLS.VEHICLES_ID(vehicleId));

// Device CRUD operations
const fetchDevicesAPI = () =>
  API.get<PaginatedResponse<Device>>(URLS.DEVICES);

const fetchDeviceDetailAPI = (deviceId: number) =>
  API.get<Device>(URLS.DEVICE_ID(deviceId));

const createDeviceAPI = (deviceData: DeviceRequest) =>
  API.post<Device>(URLS.DEVICES, deviceData);

const updateDeviceAPI = (deviceId: number, deviceData: DeviceRequest) =>
  API.put<Device>(URLS.DEVICE_ID(deviceId), deviceData);

const patchDeviceAPI = (deviceId: number, deviceData: Partial<DeviceRequest>) =>
  API.patch<Device>(URLS.DEVICE_ID(deviceId), deviceData);

const deleteDeviceAPI = (deviceId: number) =>
  API.delete(URLS.DEVICE_ID(deviceId));

interface DeviceReadingsQueryOptions {
  page?: number;
  pageSize?: number;
  subReadings?: 'any' | 'only' | 'none';
  when?: 'latestMissingSubReadings' | null;
}

const fetchDeviceReadingsAPI = (
  deviceId: number,
  options: DeviceReadingsQueryOptions = {}
) => {
  const { page = 1, pageSize = 10, subReadings, when } = options;
  // const { page = 1, pageSize = 10 } = options;
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  if (subReadings) params.set('subReadings', subReadings);
  if (when) params.set('when', when);
  const queryString = params.toString();
  return API.get<PaginatedResponse<SensorReading>>(
    `${URLS.DEVICE_READINGS(deviceId)}?${queryString}`
  );
};

const fetchBabiesAPI = () =>
  API.get<Baby[]>(URLS.BABIES);

const fetchBabyDetailAPI = (babyId: number) =>
  API.get<BabyDetail>(URLS.BABIES_ID(babyId));

const createBabyAPI = (babyData: BabyRequest) => {
  const formData = new FormData();
  formData.append('name', babyData.name);
  formData.append('date_of_birth', babyData.date_of_birth);

  if (babyData.medical_notes) {
    formData.append('medical_notes', babyData.medical_notes);
  }

  if (babyData.sub_devices_ids && babyData.sub_devices_ids.length > 0) {
    formData.append('sub_devices_ids', babyData.sub_devices_ids.join(','));
  }

  if (babyData.avatar_url) {
    formData.append('avatar_url', babyData.avatar_url);
  }

  return API.post<Baby>(URLS.BABIES, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

const updateBabyAPI = (babyId: number, babyData: Partial<BabyRequest>) => {
  const formData = new FormData();

  if (babyData.name) {
    formData.append('name', babyData.name);
  }
  if (babyData.date_of_birth) {
    formData.append('date_of_birth', babyData.date_of_birth);
  }
  if (babyData.medical_notes) {
    formData.append('medical_notes', babyData.medical_notes);
  }
  if (babyData.sub_devices_ids && babyData.sub_devices_ids.length > 0) {
    formData.append('sub_devices_ids', babyData.sub_devices_ids.join(','));
  }
  if (babyData.avatar_url) {
    formData.append('avatar_url', babyData.avatar_url);
  }

  return API.patch<Baby>(URLS.BABIES_ID(babyId), formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

const fetchSubDeviceDetailAPI = async (id: string) => (
  API.get<SubDevice>(`/monitoring/sub-devices/${id}`)
);

type VehicleDataType = {
  registration_no: string;
  brand: string;
  model: string;
  color?: string;
  manufacture_year: number;
  device_name?: string;
};

type RegisterDevicePayload = {
  qr_code_data: string | Record<string, any>;
  vehicle_data: VehicleDataType;
};

type RegisterDeviceResponse = {
  success: boolean;
  device_id: number;
  vehicle_id: number;
  message: string;
};

/**
 * Registers a new main device and associated vehicle.
 */
const registerDeviceAPI = async (payload: RegisterDevicePayload) => (
  API.post<RegisterDeviceResponse>('/monitoring/register-device', payload)
);

type RegisterDeviceFromQRPayload = {
  device_role: 'main' | 'sub';
  qr_code_data: string;
  vehicle_data?: VehicleDataType;
  device_id?: number;
  sub_device_name?: string;
  sub_device_type?: SubDeviceTypeEnum;
};

/**
 * Registers a main or sub-device from QR code with explicit device role.
 */
const registerDeviceFromQRAPI = async (payload: RegisterDeviceFromQRPayload) => (
  API.post<RegisterDeviceFromQRResponse>('/monitoring/register-device-qr', payload)
);

type SubDeviceTypeEnum = 'EBLANKET' | 'BELTCLIP';

type RegisterDeviceFromQRMainResponse = {
  success: true;
  device_role: 'main';
  device_type: 'COMMERCIAL' | 'PERSONAL';
  device_id: number;
  vehicle_id: number;
  message: string;
};

type RegisterDeviceFromQRSubResponse = {
  success: true;
  device_role: 'sub';
  sub_device_id: number;
  attached_to_device: number | null;
  message: string;
};

type RegisterDeviceFromQRErrorResponse = {
  error: string;
  details?: Record<string, string[]>;
};

type RegisterDeviceFromQRResponse =
  | RegisterDeviceFromQRMainResponse
  | RegisterDeviceFromQRSubResponse
  | RegisterDeviceFromQRErrorResponse;

type RegisterSubDevicePayload = {
  mac_address: string;
  sub_device_type: SubDeviceTypeEnum;
  device_id: number;
  sub_device_name?: string;
};

/**
 * Registers a new sub-device and links it to an existing main device.
 */
const registerSubDeviceAPI = async (payload: RegisterSubDevicePayload) => (
  API.post<SubDevice>('/monitoring/sub-devices/', payload)
);

const fetchEnvironmentMonitorsAPI = () =>
  API.get<EnvironmentMonitorsResponse>(URLS.ENVIRONMENT_MONITORS);

// Interface for latest readings response
interface LatestReadingResponse {
  device_id: number;
  device_name: string | null;
  reading: SensorReading | null;
}

/**
 * Get latest reading for a specific device
 */
const fetchLatestReadingsAPI = async (deviceId: number) =>
  API.get<LatestReadingResponse>(`/monitoring/latest-readings/${deviceId}`);

/**
 * Get babies latest readings grouped by vehicle
 */
const fetchBabiesLatestReadingsByVehicleAPI = () =>
  API.get<BabiesLatestReadingsByVehicleResponse>(URLS.BABIES_LATEST_READINGS_BY_VEHICLE);


export {
  type SensorReading,
  type EscalationLevel,
  type PaginatedResponse,
  type Device,
  type DeviceWithReadings,
  type Vehicle,
  type VehicleDetail,
  type SubDevice,
  type BabyDetailSubDeviceReading,
  type BabyDetailSubDevice,
  type BabyDetailMonitor,
  type Baby,
  type BabyDetail,
  type DeviceRequest,
  type BabyRequest,
  type SubDeviceReading,
  type VehicleDataType,
  type RegisterDevicePayload,
  type RegisterDeviceResponse,
  type RegisterSubDevicePayload,
  type SubDeviceTypeEnum,
  type RegisterDeviceFromQRPayload,
  type RegisterDeviceFromQRResponse,
  type RegisterDeviceFromQRMainResponse,
  type RegisterDeviceFromQRSubResponse,
  type RegisterDeviceFromQRErrorResponse,
  type EnvironmentMonitorSubDevice,
  type EnvironmentMonitorDevice,
  type EnvironmentMonitorsResponse,
  type LatestReadingResponse,
  type VehicleInfo,
  type LocationInfo,
  type ConnectedDevice,
  type LatestSubDeviceReading,
  type BabyWithDeviceAndReading,
  type VehicleWithBabiesAndReadings,
  type BabiesLatestReadingsByVehicleResponse,
  type DeviceReadingsQueryOptions,

  registerDeviceAPI,
  registerDeviceFromQRAPI,
  registerSubDeviceAPI,
  fetchVehicleDetailAPI,
  fetchDevicesAPI,
  fetchDeviceDetailAPI,
  createDeviceAPI,
  updateDeviceAPI,
  patchDeviceAPI,
  deleteDeviceAPI,
  fetchDeviceReadingsAPI,
  fetchBabiesAPI,
  fetchBabyDetailAPI,
  createBabyAPI,
  updateBabyAPI,
  fetchSubDeviceDetailAPI,
  fetchEnvironmentMonitorsAPI,
  fetchLatestReadingsAPI,
  fetchBabiesLatestReadingsByVehicleAPI,
}
